import {
  getFontCss,
  styleConstants,
  themeConstants,
} from '@paywithextend/components'
import React, { KeyboardEvent, useRef, useState } from 'react'
import HoverTooltip from 'src/components/HoverTooltip/HoverTooltip'
import SvgUpdate from 'src/components/Svgs/SvgUpdate'
import { useTranslate } from 'src/localization/Trns'
import { css } from 'styled-components'

interface TextWithUpdateInputFieldProps {
  value: string
  characterLimit?: number
  onSave: (value: string) => void
  maxWidth?: number
  isEditable: boolean
  tooltipText: React.ReactNode
}

const TextWithUpdateInputField: React.FC<TextWithUpdateInputFieldProps> = (
  props,
): React.ReactElement => {
  const translate = useTranslate()

  const [value, setValue] = useState<string>(props.value)
  const [isFocus, setIsFocus] = useState<boolean>(false)
  const [hover, setHover] = useState<boolean>(false)
  const [showCharacterLimitWarning, setShowCharacterLimitWarning] =
    useState<boolean>(false)

  const inputRef = useRef<HTMLSpanElement>(null)

  const handleOnKeyDown = (event: KeyboardEvent<HTMLSpanElement>): void => {
    const isUnderCharacterLimit =
      props.characterLimit === undefined ||
      props.characterLimit >
        ((event.target as HTMLElement)?.textContent?.length ?? 0)

    if (event.key === 'Enter') {
      event.preventDefault()
      if (inputRef.current && inputRef.current.innerText.length > 0) {
        setValue(inputRef.current?.innerText)
        props.onSave(inputRef.current?.innerText)
      }
      inputRef.current?.blur()
      setIsFocus(false)
    } else if (
      event.key === 'Backspace' ||
      event.key === 'Delete' ||
      isUnderCharacterLimit
    ) {
      showCharacterLimitWarning && setShowCharacterLimitWarning(false)
    } else {
      if (!isUnderCharacterLimit) {
        setShowCharacterLimitWarning(true)
        setTimeout((): void => setShowCharacterLimitWarning(false), 3000)
      }
      if (
        (event.keyCode >= 48 && event.keyCode <= 90 && !event.metaKey) ||
        event.keyCode === 32
      ) {
        event.preventDefault()
      }
    }
  }

  const handleTextClickOpen = (): void => {
    inputRef.current?.focus()
    setIsFocus(true)
  }

  const handleOutOfFocus = (): void => {
    if (inputRef.current) inputRef.current.innerText = props.value
    setIsFocus(false)
  }

  if (props.isEditable) {
    return (
      // eslint-disable-next-line jsx-a11y/click-events-have-key-events
      <div
        onClick={handleTextClickOpen}
        role={'button'}
        tabIndex={0}
        onMouseEnter={(): void => setHover(true)}
        onMouseLeave={(): void => setHover(false)}
        css={css`
          display: flex;
          align-items: center;
          column-gap: 6px;
          position: relative;
        `}
      >
        <div
          css={css`
            position: relative;
            display: flex;
          `}
        >
          <span
            role={'textbox'}
            onKeyDown={handleOnKeyDown}
            onBlur={handleOutOfFocus}
            contentEditable
            spellCheck={false}
            suppressContentEditableWarning
            tabIndex={0}
            ref={inputRef}
            css={css`
              ${props.maxWidth && `max-width: ${props.maxWidth}px;`}
              border-radius: 4px;
              padding: 4px;
              &:focus {
                outline: 1px solid
                  ${showCharacterLimitWarning
                    ? styleConstants.colors.red
                    : themeConstants.colors.cta};
                white-space: nowrap;
                overflow: hidden;
                background-color: ${styleConstants.colors.white};
              }
              ${!isFocus &&
              `
                &:hover {
                  background-color: ${styleConstants.colors.gray_medium};
                }
              `}
            `}
          >
            {props.value}
          </span>
          <HoverTooltip
            show={hover && !isFocus}
            tooltipText={props.tooltipText}
          />
        </div>
        <SvgUpdate
          css={css`
            margin-top: 6px;
            transform: scale(calc(18 / 24));
            cursor: pointer;
          `}
        />
        {isFocus && (
          <span
            css={css`
              position: absolute;
              bottom: -22px;
              width: max-content;
              ${getFontCss(
                12,
                showCharacterLimitWarning
                  ? styleConstants.colors.red
                  : styleConstants.colors.gray_darkest,
                styleConstants.fontFamilies.regular,
              )}
            `}
          >
            {props.characterLimit && showCharacterLimitWarning
              ? translate('${characterLimit} character limit reached', {
                  characterLimit: props.characterLimit.toString(),
                })
              : translate('Press Enter to save')}
          </span>
        )}
      </div>
    )
  }

  return (
    <span
      css={css`
        word-break: break-word;
        margin-right: 10px;
      `}
    >
      {value}
    </span>
  )
}

export default React.memo(TextWithUpdateInputField)
